/**
 * The employee-facing Employee Directory and Org Chart — "find a colleague" for everyone in the
 * organisation, not the admin Employee List.
 *
 * WHY A SEPARATE CONTRACT, not GET /employees or lookup.searchColleagues:
 *   - GET /employees returns the full `Employee` record (personal phone, username, employment type,
 *     grade, cost centre ...) and filters every row through `employee:read`, which a plain employee
 *     holds at `self` scope — they would see only themselves. Widening that grant would widen the
 *     whole record, not just the fields a directory needs.
 *   - lookup.searchColleagues is a type-ahead for pickers keyed by PERSON id (a delegate, "filing
 *     for"): capped at 20 rows, no filters, no pagination, and deliberately no contact details.
 *     Growing it into a directory would push work email and phone into every picker too.
 *
 * So the directory has its own DTO, and the DTO is the safety boundary: `directoryEntrySchema`
 * names ONLY work-safe fields. No personal email or phone, DOB, address, emergency contact, grade,
 * pay, or any PII (CLAUDE.md invariant 7, ADR 0023 — nothing here exposes anyone's pay). A column
 * added to `people` later cannot reach this response without someone adding it to the schema below.
 *
 * GATE: `employee.directory:read`, held by the base employee tier (so every role that spreads it),
 * rather than `null`. The data is the same for everyone who may see it — there is no per-row scope —
 * but a permission lets an organisation withdraw the directory from a role (external contractors,
 * a custom role for interns) on Roles & Permissions, which a signed-in-only route could never allow.
 * The org chart is gated on the existing `org_chart:read` ("See the reporting structure") for the
 * same reason.
 *
 * "Current" department, location, designation and manager are the assignment in effect on the
 * CALLER's "today" in their location's timezone — one day for the whole query (ADR 0025 §5, lists
 * and team views). Exited, rescinded, pre-joining and archived employments are not listed.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  errorBodySchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

/**
 * One colleague as the directory and the org chart show them. Keyed by EMPLOYMENT (the job), like
 * the admin Employee List and the org chart's reporting lines — a manager is an employment.
 */
export const directoryEntrySchema = z.object({
  employmentId: uuidSchema,
  personId: uuidSchema,
  /** `displayName` when set, otherwise "First Last" — resolved server-side. */
  name: z.string(),
  employeeCode: z.string(),
  photoUrl: z.string().nullable(),
  designation: z.string().nullable(),
  departmentId: uuidSchema.nullable(),
  departmentName: z.string().nullable(),
  locationId: uuidSchema.nullable(),
  locationName: z.string().nullable(),
  legalEntityId: uuidSchema,
  legalEntityName: z.string(),
  /** The WORK email (the login identity) — never `personalEmail`. Null for a phone-only employee. */
  workEmail: z.string().nullable(),
  /** The WORK phone (desk / extension) — never the personal `phone`. */
  workPhone: z.string().nullable(),
  managerEmploymentId: uuidSchema.nullable(),
  managerName: z.string().nullable(),
  /** How many listed colleagues report to this one right now — drives the org chart's expanders. */
  directReportCount: z.number().int().nonnegative(),
});
export type DirectoryEntry = z.infer<typeof directoryEntrySchema>;

export const listDirectoryQuerySchema = paginationQuerySchema.extend({
  /** Matches first, last or display name, employee code, designation, or work email. */
  q: z.string().trim().max(100).optional(),
  departmentId: uuidSchema.optional(),
  locationId: uuidSchema.optional(),
  legalEntityId: uuidSchema.optional(),
});

export const orgChartQuerySchema = z.object({
  /** Whose reporting line to show. Omit for the caller's own. */
  employmentId: uuidSchema.optional(),
});

/**
 * One node of the reporting tree with everything needed to expand it in both directions: the chain
 * above it and the people directly below it. Expanding a report further down is another call with
 * that report's `employmentId`; walking up is already in `managers`.
 */
export const orgChartSchema = z.object({
  focus: directoryEntrySchema,
  /** The management chain above `focus`, TOP-DOWN: the root first, the direct manager last. */
  managers: z.array(directoryEntrySchema),
  /** The people who report directly to `focus`, by name. */
  directReports: z.array(directoryEntrySchema),
});
export type OrgChart = z.infer<typeof orgChartSchema>;

export const directoryContract = c.router(
  {
    list: {
      method: 'GET',
      path: '/people',
      query: listDirectoryQuerySchema,
      responses: {
        200: paginatedSchema(directoryEntrySchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'Employee Directory: search colleagues by name, code, designation or email, filter by department/location/entity — work contact details only',
      metadata: routeMeta('employee.directory:read'),
    },
    get: {
      method: 'GET',
      path: '/people/:employmentId',
      pathParams: z.object({ employmentId: uuidSchema }),
      responses: {
        200: directoryEntrySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "One colleague's directory card — work contact details, team and manager",
      metadata: routeMeta('employee.directory:read'),
    },
    orgChartRoots: {
      method: 'GET',
      path: '/org-chart/roots',
      responses: {
        200: z.object({ items: z.array(directoryEntrySchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'The top of the organisation: listed colleagues with no current manager (Keka "Top of the Org")',
      metadata: routeMeta('org_chart:read'),
    },
    orgChart: {
      method: 'GET',
      path: '/org-chart',
      query: orgChartQuerySchema,
      responses: {
        200: orgChartSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        "Reporting line around one employee (the caller's own by default): their managers up to the top and their direct reports",
      metadata: routeMeta('org_chart:read'),
    },
  },
  { pathPrefix: '/directory' },
);
